import React, { useState, useEffect } from "react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell,
} from "recharts";
import api from "../lib/api";

/* ─── helpers ─────────────────────────────────────────────── */


const fmt = (n: number, d = 0) => n.toLocaleString("en-KE", { minimumFractionDigits: d, maximumFractionDigits: d });

/* ─── chart tooltip ──────────────────────────────────────── */
function ChartTooltip({ active, payload, label, prefix = "", suffix = "" }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0f2557] border border-white/10 rounded-xl px-3.5 py-2.5 shadow-xl text-xs">
      <p className="text-blue-300/70 mb-1.5 font-medium">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} className="flex items-center gap-2 text-white">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
          <span className="text-blue-200/70">{p.name}:</span>
          <span className="font-semibold">{prefix}{fmt(p.value, 1)}{suffix}</span>
        </p>
      ))}
    </div>
  );
}

/* ─── stat card ──────────────────────────────────────────── */
function StatCard({ label, value, sub, icon, color }: { label: string; value: string; sub?: string; icon: React.ReactNode; color: string }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[11.5px] text-gray-400 font-medium uppercase tracking-widest truncate">{label}</p>
        <p className="text-[22px] font-bold text-gray-800 leading-tight mt-0.5">{value}</p>
        {sub && <p className="text-[11.5px] text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

/* ─── section header ─────────────────────────────────────── */
function SectionHeader({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="mb-4">
      <h2 className="text-[15px] font-bold text-gray-800">{title}</h2>
      {sub && <p className="text-[12px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

/* ─── mock data generators ───────────────────────────────── */
function genMonthly() {
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const now = new Date();
  return Array.from({ length: 12 }, (_, i) => {
    const m = (now.getMonth() - 11 + i + 12) % 12;
    return {
      month: months[m],
      units: Math.round(80 + Math.random() * 60),
      spend: Math.round(400 + Math.random() * 400),
    };
  });
}

function genDaily() {
  return Array.from({ length: 30 }, (_, i) => ({
    day: `Day ${i + 1}`,
    units: parseFloat((2 + Math.random() * 3).toFixed(2)),
    flow: parseFloat((0.3 + Math.random() * 0.5).toFixed(2)),
  }));
}

function genHourly() {
  return Array.from({ length: 24 }, (_, i) => ({
    hour: `${i.toString().padStart(2, "0")}:00`,
    usage: parseFloat((i >= 5 && i <= 9 ? 0.4 + Math.random() * 0.6 : i >= 17 && i <= 21 ? 0.5 + Math.random() * 0.7 : 0.05 + Math.random() * 0.2).toFixed(2)),
  }));
}

const PIE_COLORS = ["#0ea5e9", "#6366f1", "#10b981", "#f59e0b"];

/* ─── range tab ──────────────────────────────────────────── */
function RangeTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all ${
        active ? "bg-sky-500 text-white shadow-sm" : "text-gray-500 hover:bg-gray-100"
      }`}
    >
      {label}
    </button>
  );
}

/* ─── skeleton ───────────────────────────────────────────── */
function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-xl ${className}`} />;
}

/* ═══════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════ */
export default function TenantAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<"daily" | "monthly">("monthly");
  const [meters, setMeters] = useState<any[]>([]);
  const [selectedMeter, setSelectedMeter] = useState<number | null>(null);

  const monthly = genMonthly();
  const daily = genDaily();
  const hourly = genHourly();

  const totalUnits = monthly.reduce((s, r) => s + r.units, 0);
  const totalSpend = monthly.reduce((s, r) => s + r.spend, 0);
  const avgMonthly = Math.round(totalUnits / 12);
  const peakMonth = [...monthly].sort((a, b) => b.units - a.units)[0];

  const categoryData = [
    { name: "Morning (5–9am)", value: 28 },
    { name: "Afternoon (10am–4pm)", value: 19 },
    { name: "Evening (5–9pm)", value: 38 },
    { name: "Night (10pm–4am)", value: 15 },
  ];

  useEffect(() => {
    api.get(`/meters/`)
      .then((r) => {
        setMeters(r.data?.results ?? r.data ?? []);
        if ((r.data?.results ?? r.data ?? []).length) setSelectedMeter((r.data?.results ?? r.data)[0].id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-56" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-72" />
        <Skeleton className="h-72" />
      </div>
    );
  }

  return (
    <div className="space-y-7">

      {/* ── header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold text-gray-800 leading-tight">My Analytics</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">Detailed view of your water consumption and spending</p>
        </div>
        {/* Meter picker */}
        {meters.length > 0 && (
          <select
            value={selectedMeter ?? ""}
            onChange={(e) => setSelectedMeter(Number(e.target.value))}
            className="border border-gray-200 rounded-xl px-3 py-2 text-[13px] font-medium text-gray-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
          >
            {meters.map((m) => (
              <option key={m.id} value={m.id}>
                Meter — {m.serial_number}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* ── KPI strip ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Units (12mo)"
          value={`${fmt(totalUnits)} L`}
          sub="All meters combined"
          color="bg-sky-50"
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth={1.8} className="w-5 h-5"><path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" strokeLinejoin="round"/></svg>}
        />
        <StatCard
          label="Total Spend (12mo)"
          value={`KES ${fmt(totalSpend)}`}
          sub="All top-ups this year"
          color="bg-emerald-50"
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth={1.8} className="w-5 h-5"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20" strokeLinecap="round"/><path d="M6 15h4" strokeLinecap="round"/></svg>}
        />
        <StatCard
          label="Avg Monthly Usage"
          value={`${fmt(avgMonthly)} L`}
          sub="Based on last 12 months"
          color="bg-violet-50"
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth={1.8} className="w-5 h-5"><path d="M3 17l4-8 4 5 3-3 4 6" strokeLinecap="round" strokeLinejoin="round"/><path d="M3 21h18" strokeLinecap="round"/></svg>}
        />
        <StatCard
          label="Peak Month"
          value={peakMonth.month}
          sub={`${fmt(peakMonth.units)} L consumed`}
          color="bg-amber-50"
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth={1.8} className="w-5 h-5"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round"/></svg>}
        />
      </div>

      {/* ── monthly consumption + spend ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Area — units */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <SectionHeader title="12-Month Consumption" sub="Water units used per month (litres)" />
            <div className="flex gap-1 bg-gray-50 rounded-lg p-1">
              <RangeTab label="Monthly" active={range === "monthly"} onClick={() => setRange("monthly")} />
              <RangeTab label="Daily" active={range === "daily"} onClick={() => setRange("daily")} />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            {range === "monthly" ? (
              <AreaChart data={monthly} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="unitGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip suffix=" L" />} />
                <Area type="monotone" dataKey="units" name="Units" stroke="#0ea5e9" strokeWidth={2} fill="url(#unitGrad)" dot={false} activeDot={{ r: 4, fill: "#0ea5e9" }} />
              </AreaChart>
            ) : (
              <AreaChart data={daily} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="unitGrad2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={4} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip suffix=" L" />} />
                <Area type="monotone" dataKey="units" name="Units" stroke="#0ea5e9" strokeWidth={2} fill="url(#unitGrad2)" dot={false} activeDot={{ r: 4, fill: "#0ea5e9" }} />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Pie — usage by time of day */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <SectionHeader title="Usage by Time of Day" sub="When you use the most water" />
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={categoryData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                {categoryData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v: any) => [`${v}%`, ""]} contentStyle={{ background: "#0f2557", border: "none", borderRadius: 10, fontSize: 12 }} itemStyle={{ color: "#e2e8f0" }} labelStyle={{ color: "#94a3b8" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-3 space-y-2">
            {categoryData.map((c, i) => (
              <div key={c.name} className="flex items-center gap-2 text-[12px]">
                <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: PIE_COLORS[i] }} />
                <span className="text-gray-500 flex-1 truncate">{c.name}</span>
                <span className="font-semibold text-gray-700">{c.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── monthly spend bar + hourly line ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Bar — monthly spend */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <SectionHeader title="Monthly Spending (KES)" sub="Token top-up amount per month" />
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthly} margin={{ top: 4, right: 8, left: -10, bottom: 0 }} barSize={18}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip prefix="KES " />} />
              <Bar dataKey="spend" name="Spend" fill="#0ea5e9" radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Line — hourly usage pattern */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <SectionHeader title="Typical Daily Pattern" sub="Average hourly consumption (litres)" />
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={hourly} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="hour" tick={{ fontSize: 9, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={3} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip suffix=" L" />} />
              <Line type="monotone" dataKey="usage" name="Usage" stroke="#6366f1" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: "#6366f1" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── consumption vs spend dual axis ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <SectionHeader title="Consumption vs Spend Correlation" sub="Compare units used against amount paid each month" />
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={monthly} margin={{ top: 4, right: 8, left: -10, bottom: 0 }} barSize={14}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTooltip />} />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: "#64748b" }} />
            <Bar yAxisId="left" dataKey="units" name="Units (L)" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
            <Bar yAxisId="right" dataKey="spend" name="Spend (KES)" fill="#6366f1" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ── insights panel ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <SectionHeader title="Usage Insights" sub="Smart tips based on your consumption patterns" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            {
              icon: "💧",
              color: "bg-sky-50 border-sky-100",
              title: "Peak Usage Window",
              body: "You consume the most water between 5–9 PM. Consider spreading usage throughout the day to improve meter pressure.",
            },
            {
              icon: "📈",
              color: "bg-violet-50 border-violet-100",
              title: "Above Average Month",
              body: `Your usage in ${peakMonth.month} was ${fmt(peakMonth.units)} L — your highest this year. Review if any leaks occurred.`,
            },
            {
              icon: "✅",
              color: "bg-emerald-50 border-emerald-100",
              title: "Consistent Spender",
              body: `You top up an average of KES ${fmt(Math.round(totalSpend / 12))} per month. Set up auto top-up to never run dry.`,
            },
          ].map((ins) => (
            <div key={ins.title} className={`rounded-xl border p-4 ${ins.color}`}>
              <div className="text-2xl mb-2">{ins.icon}</div>
              <p className="text-[13px] font-semibold text-gray-800 mb-1">{ins.title}</p>
              <p className="text-[12px] text-gray-500 leading-relaxed">{ins.body}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
